import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { sanitize } from '../../utils/common';
import MeetingServices from '../../services/meeting.services';

const ROLE_EMPLOYEE = 1;
const ROLE_REVIEWER = 2;
const ANSWERED_BY_BOTH = 3;

const isAssigned = (answeredBy, role) => answeredBy === ANSWERED_BY_BOTH || answeredBy === role;

const StructuredReviewSummary = ({ meetingId }) => {
  const { t } = useTranslation();
  const [reviewAnswers, setReviewAnswers] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!window.wbConfig?.structuredReviewQuestionsEnabled) return undefined;
    const store = (data) => {
      if (mounted) setReviewAnswers(data);
    };
    new MeetingServices()
      .getReviewAnswersSummary(meetingId)
      .then((response) => {
        const data = response?.data?.data;
        store(data?.sections?.length ? data : null);
      })
      .catch(() => store(null));
    return () => {
      mounted = false;
    };
  }, [meetingId]);

  if (!reviewAnswers?.sections?.length) {
    return null;
  }
  const {
    sections,
    answers = [],
    employee,
    reviewer,
    memberSubmittedAt,
    managerSubmittedAt
  } = reviewAnswers;
  const people = { [ROLE_EMPLOYEE]: employee, [ROLE_REVIEWER]: reviewer };
  const submittedAt = { [ROLE_EMPLOYEE]: memberSubmittedAt, [ROLE_REVIEWER]: managerSubmittedAt };
  const hasQuestions = role =>
    sections.some(section => section.questions.some(q => isAssigned(q.answeredBy, role)));
  const assignedRoles = answeredBy =>
    [ROLE_EMPLOYEE, ROLE_REVIEWER].filter(role => isAssigned(answeredBy, role));
  const assignees = answeredBy =>
    assignedRoles(answeredBy)
      .map(role => people[role].name)
      .join(` ${t('and')} `);
  const submittedOn = timestamp => {
    if (!timestamp) {
      return t('(not submitted)');
    }
    return t('on {{date}}', { date: moment(timestamp * 1000).format('MMM DD, YYYY') });
  };
  const submitters = [ROLE_EMPLOYEE, ROLE_REVIEWER].filter(hasQuestions);
  const submittedLine = submitters.length === 2
    ? t('Submitted by {{employee}} {{employeeDate}} and {{reviewer}} {{reviewerDate}}', {
      employee: employee.name,
      employeeDate: submittedOn(memberSubmittedAt),
      reviewer: reviewer.name,
      reviewerDate: submittedOn(managerSubmittedAt)
    })
    : t('Submitted by {{name}} {{date}}', {
      name: people[submitters[0]].name,
      date: submittedOn(submittedAt[submitters[0]])
    });
  return (
    <div className="structuredReviewSummary">
      <div className="meetingPublishAgenda">{t('STRUCTURED REVIEW QUESTIONS')}</div>
      <div className="agendaDescriptionMember takeAwayDesc">
        {submittedLine}
      </div>
      {sections.map(section => (
        <div className="structuredReviewSection" key={section.id}>
          <div className="structuredReviewSectionTitle">
            {section.title || t('Untitled section')}
          </div>
          {section.questions.map(question => (
            <div className="agendaTakeAwaysData takeAwayFont" key={question.id}>
              <div
                className="agendaDescription takeAwayDesc"
                dangerouslySetInnerHTML={{ __html: sanitize(question.question) }}
              />
              <div className="agendaDescriptionMember takeAwayDesc">
                {`${assignees(question.answeredBy)} · ${
                  question.isRequired ? t('Required') : t('Optional')
                }`}
              </div>
              {assignedRoles(question.answeredBy).map(role => {
                const answer = answers.find(
                  row => row.questionId === question.id && row.role === role
                );
                const person = answer?.user || people[role];
                const hasAnswer = Boolean(answer?.answer);
                const submitted = Boolean(submittedAt[role]);
                return (
                  <div className="structuredReviewAnswer" key={role}>
                    <div className="attendeeDetails">
                      <div className="attendee-img">
                        {person.picture ? (
                          <img
                            src={person.picture}
                            alt=""
                            className="fitProfileImage goal-user-picture attendeePic roundFramePhoto"
                          />
                        ) : (
                          <span className="attendeePic structuredReviewAvatar" />
                        )}
                      </div>
                      <div className="attendeeName">{person.name}</div>
                    </div>
                    {hasAnswer ? (
                      <>
                        <div
                          className="agendaDescription takeAwayDesc"
                          dangerouslySetInnerHTML={{ __html: sanitize(answer.answer) }}
                        />
                        <div className="agendaDescriptionMember takeAwayDesc">
                          {`${person.name} ${moment(
                            (answer.modifiedAt || answer.createdAt) * 1000
                          ).format('hh:mm a MMM DD,YYYY')}`}
                          {answer.modifiedAt ? ` ${t('(edited)')}` : ''}
                        </div>
                      </>
                    ) : (
                      <div className="agendaDescriptionMember takeAwayDesc">
                        {submitted ? t('Not answered') : t('Not submitted')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default StructuredReviewSummary;
